import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useState, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import {
  Modal,
  Button,
  Checkbox,
  Chip,
  Icon,
  Input,
  Select,
} from "@legal-500/legal500-design-system";
import { FilterRulesEditor, type FilterRulesEditorHandle } from "./FilterRulesEditor";
import { SubjectLabelField } from "./SubjectLabelField";
import { DescriptionField } from "./DescriptionField";
import type {
  QuestionType,
  LibraryQuestion,
  CreateQuestionBody,
  UpdateQuestionBody,
  DisplayFilterRule,
  LocalizedQuestionText,
} from "@/api/survey-library.service";
import { getLocalizedTextDefault } from "@/api/survey-library.service";
import { surveysService } from "@/api/surveys.service";
import type { DisplayRuleOptions } from "@/api/surveyTaxonomy.service";
import {
  SURVEY_QUESTION_TYPE_CONFIG,
  SURVEY_QUESTION_TYPE_OPTIONS,
  getChoiceOptionsValidationError,
  isMultiTestimonialType,
  isTestimonialType,
  supportsNoExperienceLabel,
} from "../questionTypes";
import { getLanguageFlag } from "../Details/surveyDetails.utils";
import styles from "./QuestionLibrary.module.css";

type RangeVariantType = "range-10" | "range-100";
const EMPTY_TRANSLATABLE_LANGUAGES: string[] = [];

type RangeLabelRowState = {
  id: string;
  defaultText: string;
};

function emptyRangeLabelTranslationMatrix(languages: string[]) {
  return languages.reduce(
    (acc, language) => {
      acc[language] = {};
      return acc;
    },
    {} as Record<string, Record<string, string>>,
  );
}

function buildLibraryDescriptionPayload(
  defaultHint: string,
  perLang: Record<string, string>,
  langs: string[],
): LocalizedQuestionText | null {
  const parts: Record<string, string> = {};
  const d = defaultHint.trim();
  if (d) parts.default = d;
  for (const lang of langs) {
    const t = (perLang[lang] ?? "").trim();
    if (t) parts[lang] = t;
  }
  if (Object.keys(parts).length === 0) return null;
  if (!parts.default) {
    const first = langs.map(lang => parts[lang]).find(Boolean) ?? Object.values(parts)[0];
    if (!first) return null;
    return { default: first, ...parts } as LocalizedQuestionText;
  }
  return parts as LocalizedQuestionText;
}

/**
 * Builds the per-locale `subjectLabel` payload for `multi-testimonial` questions.
 * Same localized shape as the description payload: `{ default, [lang] }` with empty
 * values pruned, returning null when nothing is set.
 */
function buildSubjectLabelPayload(
  defaultText: string,
  perLang: Record<string, string>,
  langs: string[],
): LocalizedQuestionText | null {
  return buildLibraryDescriptionPayload(defaultText, perLang, langs);
}

const DEFAULT_NO_EXPERIENCE_LABEL = "No experience";

function pickLocalizedTranslations(
  value: LocalizedQuestionText | null | undefined,
  langs: string[],
): Record<string, string> {
  return langs.reduce(
    (acc, language) => {
      const translated = value && typeof value === "object" ? value[language] : undefined;
      acc[language] = typeof translated === "string" ? translated : "";
      return acc;
    },
    {} as Record<string, string>,
  );
}

async function translateIntoLanguages(
  source: string,
  languages: string[],
): Promise<{ translations: Record<string, string>; failedCount: number }> {
  const results = await Promise.allSettled(
    languages.map(language =>
      surveysService.translate(source, language).then(({ translatedText }) => ({
        language,
        translatedText,
      })),
    ),
  );
  const translations: Record<string, string> = {};
  for (const result of results) {
    if (result.status !== "fulfilled") continue;
    translations[result.value.language] = result.value.translatedText;
  }
  return {
    translations,
    failedCount: results.filter(result => result.status === "rejected").length,
  };
}

type BulkAutoTranslateQuestionFieldsParams = {
  sourceText: string;
  translatableLanguages: string[];
  fillMissingOnly: boolean;
  baseTranslations: Record<string, string>;
  baseRangeLabelTranslations: Record<string, Record<string, string>>;
  showRangeLabelsSection: boolean;
  rangeLabelRows: RangeLabelRowState[];
  descriptionDefault: string;
  baseDescriptionTranslations: Record<string, string>;
  noExperienceLabelDefault: string;
  baseNoExperienceLabelTranslations: Record<string, string>;
  /** When false, skip machine translation for question text (random library UX). */
  autoTranslateQuestionText?: boolean;
};

/** Same API calls as “Auto-translate all” for title (optional), optional hint, and range labels. */
async function bulkAutoTranslateQuestionFields({
  sourceText,
  translatableLanguages,
  fillMissingOnly,
  baseTranslations,
  baseRangeLabelTranslations,
  showRangeLabelsSection,
  rangeLabelRows,
  descriptionDefault,
  baseDescriptionTranslations,
  noExperienceLabelDefault,
  baseNoExperienceLabelTranslations,
  autoTranslateQuestionText = true,
}: BulkAutoTranslateQuestionFieldsParams): Promise<{
  mergedTranslations: Record<string, string>;
  mergedRangeLabelTranslations: Record<string, Record<string, string>>;
  mergedDescriptionTranslations: Record<string, string>;
  mergedNoExperienceLabelTranslations: Record<string, string>;
  failedCount: number;
}> {
  const mergedTranslations = { ...baseTranslations };
  const mergedRangeLabelTranslations = translatableLanguages.reduce(
    (acc, language) => {
      acc[language] = { ...(baseRangeLabelTranslations[language] ?? {}) };
      return acc;
    },
    {} as Record<string, Record<string, string>>,
  );
  const mergedDescriptionTranslations = { ...baseDescriptionTranslations };
  const mergedNoExperienceLabelTranslations = { ...baseNoExperienceLabelTranslations };

  const trimmedSource = sourceText.trim();
  const langs = translatableLanguages;

  let failedCount = 0;

  if (autoTranslateQuestionText && trimmedSource && langs.length > 0) {
    const titleLanguages = langs.filter(
      language => !fillMissingOnly || !String(mergedTranslations[language] ?? "").trim(),
    );

    const titleResults = await Promise.allSettled(
      titleLanguages.map(language =>
        surveysService.translate(trimmedSource, language).then(({ translatedText }) => ({
          language,
          translatedText,
        })),
      ),
    );

    for (const result of titleResults) {
      if (result.status !== "fulfilled") continue;
      mergedTranslations[result.value.language] = result.value.translatedText;
    }

    failedCount += titleResults.filter(result => result.status === "rejected").length;
  }

  const hint = descriptionDefault.trim();
  if (autoTranslateQuestionText && hint && langs.length > 0) {
    const descLanguages = langs.filter(
      language => !fillMissingOnly || !String(mergedDescriptionTranslations[language] ?? "").trim(),
    );
    const descResults = await Promise.allSettled(
      descLanguages.map(language =>
        surveysService.translate(hint, language).then(({ translatedText }) => ({
          language,
          translatedText,
        })),
      ),
    );
    for (const result of descResults) {
      if (result.status !== "fulfilled") continue;
      mergedDescriptionTranslations[result.value.language] = result.value.translatedText;
    }
    failedCount += descResults.filter(result => result.status === "rejected").length;
  }

  if (
    showRangeLabelsSection &&
    rangeLabelRows.some(row => row.defaultText.trim()) &&
    langs.length > 0
  ) {
    const labelJobs = rangeLabelRows.flatMap(row => {
      const def = row.defaultText.trim();
      if (!def) return [];
      return langs
        .filter(
          language =>
            !fillMissingOnly ||
            !String(mergedRangeLabelTranslations[language]?.[row.id] ?? "").trim(),
        )
        .map(language =>
          surveysService.translate(def, language).then(({ translatedText }) => ({
            rowId: row.id,
            language,
            translatedText,
          })),
        );
    });

    const labelResults = await Promise.allSettled(labelJobs);
    failedCount += labelResults.filter(result => result.status === "rejected").length;

    for (const result of labelResults) {
      if (result.status !== "fulfilled") continue;
      const { rowId, language, translatedText } = result.value;
      mergedRangeLabelTranslations[language] = {
        ...(mergedRangeLabelTranslations[language] ?? {}),
        [rowId]: translatedText,
      };
    }
  }

  const noExperienceLabelSource = noExperienceLabelDefault.trim();
  if (noExperienceLabelSource && langs.length > 0) {
    const noExperienceLabelLanguages = langs.filter(
      language =>
        !fillMissingOnly || !String(mergedNoExperienceLabelTranslations[language] ?? "").trim(),
    );
    const noExperienceLabelResult = await translateIntoLanguages(
      noExperienceLabelSource,
      noExperienceLabelLanguages,
    );
    Object.assign(mergedNoExperienceLabelTranslations, noExperienceLabelResult.translations);
    failedCount += noExperienceLabelResult.failedCount;
  }

  return {
    mergedTranslations,
    mergedRangeLabelTranslations,
    mergedDescriptionTranslations,
    mergedNoExperienceLabelTranslations,
    failedCount,
  };
}

const createRangeLabelRow = (): RangeLabelRowState => ({
  id: crypto.randomUUID(),
  defaultText: "",
});

function SortableRangeLabelEditor({
  id,
  row,
  onChangeDefault,
  onRemove,
  canRemove,
}: {
  id: string;
  row: RangeLabelRowState;
  onChangeDefault: (value: string) => void;
  onRemove: () => void;
  canRemove: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.92 : undefined,
  };

  return (
    <div ref={setNodeRef} style={style} className={styles.rangeLabelRow}>
      <button
        type="button"
        className={styles.rangeLabelDragHandle}
        aria-label="Drag to reorder label"
        {...attributes}
        {...listeners}
      >
        <span aria-hidden className={styles.rangeLabelDragDots}>
          ⋮⋮
        </span>
      </button>
      <Input
        label="Label"
        value={row.defaultText}
        onChange={event => onChangeDefault(event.target.value)}
        fullWidth
        placeholder="Scale label"
      />
      <Button
        variant="outlined"
        size="small"
        color="error"
        onClick={onRemove}
        disabled={!canRemove}
        aria-label="Remove label"
      >
        <Icon icon="delete" size="small" />
      </Button>
    </div>
  );
}
const YES_NO_DEFAULT_OPTIONS = [
  { value: "Yes", label: "Yes" },
  { value: "No", label: "No" },
];

const normalizeYesNoDefaultValue = (value: string | null | undefined): string => {
  if (!value) return "";
  const normalized = value.trim().toLowerCase();
  if (["yes", "true", "1"].includes(normalized)) return "Yes";
  if (["no", "false", "0"].includes(normalized)) return "No";
  return "";
};

const isRangeVariantType = (value: QuestionType): value is RangeVariantType =>
  value === "range-10" || value === "range-100";

const isLabeledRangeType = (value: QuestionType): boolean => value === "range-100";

type QuestionFormModalProps = {
  isOpen: boolean;
  onClose: () => void;
  question: LibraryQuestion | null;
  onSave: (data: CreateQuestionBody | UpdateQuestionBody, id?: string) => void | Promise<void>;
  /** Called when onSave rejects (e.g. API error). Parent can show a toast; modal stays open and sets textError. */
  onSaveError?: (message: string) => void;
  /**
   * Label for the primary save button when creating a new question (`question` is null).
   * Defaults to "Add to Library" (question library). Survey editor passes e.g. "Add question".
   */
  createModeSaveLabel?: string;
  taxonomyOptions?: DisplayRuleOptions;
  showDisplayRules?: boolean;
  translatableLanguages?: string[];
  requireTranslations?: boolean;
  useCreateTwoStepFlow?: boolean;
  /**
   * When false (random library), question text is not machine-translated on “Next” or the
   * pre-save fill pass; staff translate the question manually or via “Auto-translate all” /
   * per-language buttons (those always translate question text when default text exists).
   */
  autoTranslateQuestionText?: boolean;
  /**
   * When false, per-language description/hint fields are hidden; translations are filled on
   * “Next”, save, and via “Auto-translate” actions without showing those inputs.
   */
  showDescriptionTranslationFields?: boolean;
};

function QuestionFormModal({
  isOpen,
  onClose,
  question,
  onSave,
  onSaveError,
  taxonomyOptions,
  showDisplayRules = true,
  translatableLanguages = EMPTY_TRANSLATABLE_LANGUAGES,
  requireTranslations = false,
  useCreateTwoStepFlow = false,
  autoTranslateQuestionText = true,
  showDescriptionTranslationFields = true,
  createModeSaveLabel,
}: QuestionFormModalProps) {
  const [textDefault, setTextDefault] = useState("");
  const [internalLabel, setInternalLabel] = useState("");
  const [translations, setTranslations] = useState<Record<string, string>>({});
  const [type, setType] = useState<QuestionType>("text");
  const [optionRows, setOptionRows] = useState<string[]>([]);
  const [newOption, setNewOption] = useState("");
  const [required, setRequired] = useState(false);
  const [defaultValue, setDefaultValue] = useState("");
  const [description, setDescription] = useState("");
  const [descriptionTranslations, setDescriptionTranslations] = useState<Record<string, string>>(
    {},
  );
  // Per-locale `subjectLabel`, only used for `multi-testimonial` questions.
  const [subjectLabelDefault, setSubjectLabelDefault] = useState("");
  const [subjectLabelTranslations, setSubjectLabelTranslations] = useState<Record<string, string>>(
    {},
  );
  const [subjectLabelTranslatingLanguage, setSubjectLabelTranslatingLanguage] = useState<
    string | null
  >(null);
  const [noExperienceEnabled, setNoExperienceEnabled] = useState(false);
  const [noExperienceLabelDefault, setNoExperienceLabelDefault] = useState("");
  const [noExperienceLabelTranslations, setNoExperienceLabelTranslations] = useState<
    Record<string, string>
  >({});
  const [noExperienceLabelTranslationSources, setNoExperienceLabelTranslationSources] = useState<
    Record<string, string>
  >({});
  const [noExperienceLabelError, setNoExperienceLabelError] = useState("");
  const [displayRules, setDisplayRules] = useState<DisplayFilterRule[]>([]);
  const [textError, setTextError] = useState("");
  const [defaultValueError, setDefaultValueError] = useState("");
  const [currentStep, setCurrentStep] = useState<1 | 2>(1);
  const [autoTranslateLoading, setAutoTranslateLoading] = useState(false);
  const [singleAutoTranslateLoading, setSingleAutoTranslateLoading] = useState<string | null>(null);
  const [rangeLabelRows, setRangeLabelRows] = useState<RangeLabelRowState[]>([]);
  const [rangeLabelTranslations, setRangeLabelTranslations] = useState<
    Record<string, Record<string, string>>
  >({});
  const [rangeLabelsError, setRangeLabelsError] = useState("");
  const [optionsError, setOptionsError] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [translationBatchLoading, setTranslationBatchLoading] = useState(false);

  const labelDragSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const isCreateMode = !question;
  const shouldUseTwoStepFlow = useCreateTwoStepFlow && isCreateMode;

  const translatableLanguagesRef = useRef(translatableLanguages);
  translatableLanguagesRef.current = translatableLanguages;

  const displayRulesEditorRef = useRef<FilterRulesEditorHandle>(null);

  useLayoutEffect(() => {
    if (!isOpen) {
      return;
    }
    const langs = translatableLanguagesRef.current;

    if (question) {
      setTextDefault(getLocalizedTextDefault(question.text));
      setInternalLabel(question.internalLabel ?? "");
      setTranslations(
        langs.reduce(
          (acc, language) => {
            acc[language] = question.text?.[language] ?? "";
            return acc;
          },
          {} as Record<string, string>,
        ),
      );
      setType(question.type);
      setOptionRows(question.options ?? []);
      setNewOption("");
      setRequired(question.required);
      setDefaultValue(
        question.type === "boolean"
          ? normalizeYesNoDefaultValue(question.defaultValue)
          : (question.defaultValue ?? ""),
      );
      setDescription(getLocalizedTextDefault(question.description ?? null));
      setDescriptionTranslations(
        langs.reduce(
          (acc, language) => {
            const desc = question.description;
            const v =
              desc && typeof desc === "object" && !Array.isArray(desc)
                ? ((desc as LocalizedQuestionText)[language] ?? "")
                : "";
            acc[language] = typeof v === "string" ? v : "";
            return acc;
          },
          {} as Record<string, string>,
        ),
      );
      setSubjectLabelDefault(getLocalizedTextDefault(question.subjectLabel ?? null));
      setSubjectLabelTranslations(
        langs.reduce(
          (acc, language) => {
            const label = question.subjectLabel;
            const v =
              label && typeof label === "object" && !Array.isArray(label)
                ? ((label as LocalizedQuestionText)[language] ?? "")
                : "";
            acc[language] = typeof v === "string" ? v : "";
            return acc;
          },
          {} as Record<string, string>,
        ),
      );
      setSubjectLabelTranslatingLanguage(null);
      setNoExperienceEnabled(
        supportsNoExperienceLabel(question.type) && Boolean(question.noExperienceLabel),
      );
      setNoExperienceLabelDefault(getLocalizedTextDefault(question.noExperienceLabel ?? null));
      const initialNoExperienceTranslations = pickLocalizedTranslations(
        question.noExperienceLabel,
        langs,
      );
      setNoExperienceLabelTranslations(initialNoExperienceTranslations);
      setNoExperienceLabelTranslationSources(
        Object.fromEntries(
          langs
            .filter(language => initialNoExperienceTranslations[language].trim())
            .map(language => [
              language,
              getLocalizedTextDefault(question.noExperienceLabel ?? null).trim(),
            ]),
        ),
      );
      setNoExperienceLabelError("");
      setDisplayRules(question.displayRules);
      setTextError("");
      setDefaultValueError("");
      setRangeLabelsError("");
      setOptionsError("");
      setCurrentStep(1);
      setAutoTranslateLoading(false);
      setSingleAutoTranslateLoading(null);

      if (
        isLabeledRangeType(question.type) &&
        question.rangeLabels &&
        question.rangeLabels.length > 0
      ) {
        const sorted = [...question.rangeLabels].sort((a, b) => a.order - b.order);
        setRangeLabelRows(
          sorted.map(rl => ({
            id: rl.id,
            defaultText: getLocalizedTextDefault(rl.label),
          })),
        );
        const rlTrans = emptyRangeLabelTranslationMatrix(langs);
        for (const rl of sorted) {
          for (const language of langs) {
            rlTrans[language][rl.id] = rl.label?.[language] ?? "";
          }
        }
        setRangeLabelTranslations(rlTrans);
      } else {
        setRangeLabelRows(
          isLabeledRangeType(question.type) ? [createRangeLabelRow(), createRangeLabelRow()] : [],
        );
        setRangeLabelTranslations(emptyRangeLabelTranslationMatrix(langs));
      }
    } else {
      setTextDefault("");
      setInternalLabel("");
      setTranslations(
        langs.reduce(
          (acc, language) => {
            acc[language] = "";
            return acc;
          },
          {} as Record<string, string>,
        ),
      );
      setType("text");
      setOptionRows([]);
      setNewOption("");
      setRequired(false);
      setDefaultValue("");
      setDescription("");
      setDescriptionTranslations(
        langs.reduce(
          (acc, language) => {
            acc[language] = "";
            return acc;
          },
          {} as Record<string, string>,
        ),
      );
      setSubjectLabelDefault("");
      setSubjectLabelTranslations(
        langs.reduce(
          (acc, language) => {
            acc[language] = "";
            return acc;
          },
          {} as Record<string, string>,
        ),
      );
      setSubjectLabelTranslatingLanguage(null);
      setNoExperienceEnabled(false);
      setNoExperienceLabelDefault("");
      setNoExperienceLabelTranslations(pickLocalizedTranslations(null, langs));
      setNoExperienceLabelTranslationSources({});
      setNoExperienceLabelError("");
      setDisplayRules([]);
      setTextError("");
      setDefaultValueError("");
      setRangeLabelsError("");
      setOptionsError("");
      setRangeLabelRows([]);
      setRangeLabelTranslations(emptyRangeLabelTranslationMatrix(langs));
      setCurrentStep(1);
      setAutoTranslateLoading(false);
      setSingleAutoTranslateLoading(null);
    }
    setTranslationBatchLoading(false);
  }, [isOpen, question]);

  const rangeLabelRowIds = useMemo(
    () => rangeLabelRows.map(row => row.id).join(","),
    [rangeLabelRows],
  );

  useEffect(() => {
    if (!isOpen) {
      setTranslationBatchLoading(false);
      return;
    }
    setTranslations(prev => {
      const next = { ...prev };
      let changed = false;
      for (const lang of translatableLanguages) {
        if (!(lang in next)) {
          next[lang] = "";
          changed = true;
        }
      }
      return changed ? next : prev;
    });

    setDescriptionTranslations(prev => {
      const next = { ...prev };
      let changed = false;
      for (const lang of translatableLanguages) {
        if (!(lang in next)) {
          next[lang] = "";
          changed = true;
        }
      }
      return changed ? next : prev;
    });

    setSubjectLabelTranslations(prev => {
      const next = { ...prev };
      let changed = false;
      for (const lang of translatableLanguages) {
        if (!(lang in next)) {
          next[lang] = "";
          changed = true;
        }
      }
      return changed ? next : prev;
    });

    setNoExperienceLabelTranslations(prev => {
      const next = { ...prev };
      let changed = false;
      for (const lang of translatableLanguages) {
        if (!(lang in next)) {
          next[lang] = "";
          changed = true;
        }
      }
      return changed ? next : prev;
    });

    if (!isLabeledRangeType(type)) {
      return;
    }

    const rowIds = rangeLabelRows.map(r => r.id);
    if (rowIds.length === 0) {
      return;
    }

    setRangeLabelTranslations(prev => {
      const next = { ...prev };
      let changed = false;
      for (const lang of translatableLanguages) {
        const langMap = { ...(next[lang] ?? {}) };
        let langChanged = false;
        for (const rowId of rowIds) {
          if (!(rowId in langMap)) {
            langMap[rowId] = "";
            langChanged = true;
          }
        }
        if (langChanged) {
          next[lang] = langMap;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [isOpen, translatableLanguages, type, rangeLabelRowIds]);

  const apiType = type;
  const showOptionsField = SURVEY_QUESTION_TYPE_CONFIG[apiType].hasOptions;
  const showYesNoDefaultField = apiType === "boolean";
  const showRangeDefaultField = isRangeVariantType(type);
  const showRangeLabelsSection = isLabeledRangeType(type);
  // Testimonial-type questions ignore referee context, so display rules do not apply.
  // Gate is additive on top of the existing `showDisplayRules` prop — callers that
  // explicitly pass `false` still suppress the editor.
  const effectiveShowDisplayRules = showDisplayRules && !isTestimonialType(type);
  // The subjectLabel editor is only meaningful for `multi-testimonial` questions.
  const showSubjectLabelField = isMultiTestimonialType(type);
  const showNoExperienceSection = supportsNoExperienceLabel(type);
  const showNoExperienceLabelField = showNoExperienceSection && noExperienceEnabled;
  const noExperienceLabelSourceForTranslate = showNoExperienceLabelField
    ? noExperienceLabelDefault
    : "";
  const availableOptions = optionRows.map(option => option.trim()).filter(Boolean);
  const hasRangeSourceForAutoTranslate =
    showRangeLabelsSection && rangeLabelRows.some(row => row.defaultText.trim());
  // A description is "present" when the default hint has text OR any per-language
  // translation does (e.g. hydrated in edit mode from an existing question).
  const descriptionHasContent =
    description.trim().length > 0 ||
    Object.values(descriptionTranslations).some(value => (value ?? "").trim().length > 0);
  // Show description-translation fields when the caller opts in (Question Library defaults
  // to true) OR whenever a description actually has content. This keeps random questions
  // (which pass showDescriptionTranslationFields={false}) from hiding description
  // translations in create step 2 and edit mode once a hint is entered, without regressing
  // callers that rely on the static prop.
  const shouldShowDescriptionTranslationFields =
    showDescriptionTranslationFields || descriptionHasContent;

  useEffect(() => {
    if (!showOptionsField && defaultValue) {
      return;
    }

    if (showOptionsField && defaultValue && !availableOptions.includes(defaultValue)) {
      setDefaultValue("");
    }
  }, [availableOptions, defaultValue, showOptionsField]);

  useEffect(() => {
    if (!showYesNoDefaultField) {
      return;
    }

    const normalizedDefault = normalizeYesNoDefaultValue(defaultValue);
    if (normalizedDefault !== defaultValue) {
      setDefaultValue(normalizedDefault);
    }
  }, [defaultValue, showYesNoDefaultField]);

  useEffect(() => {
    if (!showOptionsField) {
      setOptionRows([]);
      setNewOption("");
      setOptionsError("");
    }
  }, [showOptionsField]);

  useEffect(() => {
    if (!showRangeDefaultField || !defaultValueError) {
      return;
    }

    setDefaultValueError("");
  }, [defaultValue, defaultValueError, showRangeDefaultField, type]);

  // Tracks the previous open/question identity so this passive effect can tell an
  // initialization pass (owned by the layout-effect above) from a genuine type change.
  const prevRangeLabelInitRef = useRef<{ isOpen: boolean; question: LibraryQuestion | null }>({
    isOpen: false,
    question: null,
  });

  useEffect(() => {
    const prev = prevRangeLabelInitRef.current;
    const initializing = prev.isOpen !== isOpen || prev.question !== question;
    prevRangeLabelInitRef.current = { isOpen, question };
    if (!isOpen) {
      return;
    }
    // Effect A (the layout-effect initializer) owns setup on open / question change;
    // this effect only reacts to genuine user-driven type changes.
    if (initializing) {
      return;
    }
    if (!isLabeledRangeType(type)) {
      setRangeLabelRows([]);
      setRangeLabelTranslations(emptyRangeLabelTranslationMatrix(translatableLanguages));
      setRangeLabelsError("");
      return;
    }
    if (!question && rangeLabelRows.length === 0) {
      setRangeLabelRows([createRangeLabelRow(), createRangeLabelRow()]);
      setRangeLabelTranslations(emptyRangeLabelTranslationMatrix(translatableLanguages));
    }
  }, [isOpen, question, type, translatableLanguages, rangeLabelRows.length]);

  const handleRangeLabelsDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setRangeLabelRows(rows => {
      const oldIndex = rows.findIndex(row => row.id === active.id);
      const newIndex = rows.findIndex(row => row.id === over.id);
      if (oldIndex < 0 || newIndex < 0) return rows;
      return arrayMove(rows, oldIndex, newIndex);
    });
  };

  const applyNoExperienceLabelTranslations = (
    before: Record<string, string>,
    after: Record<string, string>,
  ) => {
    setNoExperienceLabelTranslations(after);
    const source = noExperienceLabelDefault.trim();
    setNoExperienceLabelTranslationSources(prev => {
      const next = { ...prev };
      for (const language of Object.keys(after)) {
        if ((after[language] ?? "") !== (before[language] ?? "")) next[language] = source;
      }
      return next;
    });
  };

  const validateNoExperienceLabel = (): boolean => {
    if (showNoExperienceLabelField && !noExperienceLabelDefault.trim()) {
      setNoExperienceLabelError('"No experience" label is required.');
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    const flushedRules = displayRulesEditorRef.current?.flushPendingRule() ?? null;
    const effectiveDisplayRules = flushedRules ?? displayRules;

    if (!textDefault.trim()) {
      setTextError("Question text is required");
      return;
    }

    if (!validateNoExperienceLabel()) {
      return;
    }

    let translationState = translations;
    let rangeLabelTranslationState = rangeLabelTranslations;
    let descriptionTranslationState = descriptionTranslations;
    const staleNoExperienceLanguages = showNoExperienceLabelField
      ? translatableLanguages.filter(
          language =>
            (noExperienceLabelTranslations[language] ?? "").trim() &&
            noExperienceLabelTranslationSources[language] !== noExperienceLabelDefault.trim(),
        )
      : [];
    let noExperienceLabelTranslationState = { ...noExperienceLabelTranslations };
    for (const language of staleNoExperienceLanguages) {
      noExperienceLabelTranslationState[language] = "";
    }
    const noExperienceLabelTranslationsBeforeSave = noExperienceLabelTranslationState;

    if (requireTranslations && translatableLanguages.length > 0) {
      setTranslationBatchLoading(true);
      try {
        const result = await bulkAutoTranslateQuestionFields({
          sourceText: textDefault,
          translatableLanguages,
          fillMissingOnly: true,
          baseTranslations: translations,
          baseRangeLabelTranslations: rangeLabelTranslations,
          showRangeLabelsSection,
          rangeLabelRows,
          descriptionDefault: description,
          baseDescriptionTranslations: descriptionTranslations,
          noExperienceLabelDefault: noExperienceLabelSourceForTranslate,
          baseNoExperienceLabelTranslations: noExperienceLabelTranslationState,
          autoTranslateQuestionText,
        });
        translationState = result.mergedTranslations;
        rangeLabelTranslationState = result.mergedRangeLabelTranslations;
        descriptionTranslationState = result.mergedDescriptionTranslations;
        noExperienceLabelTranslationState = result.mergedNoExperienceLabelTranslations;
        setTranslations(result.mergedTranslations);
        setRangeLabelTranslations(result.mergedRangeLabelTranslations);
        setDescriptionTranslations(result.mergedDescriptionTranslations);
        applyNoExperienceLabelTranslations(
          noExperienceLabelTranslationsBeforeSave,
          result.mergedNoExperienceLabelTranslations,
        );
        if (result.failedCount > 0) {
          setTextError(`Auto-translate completed with ${result.failedCount} failed request(s).`);
        }
      } catch {
        setTextError(
          'Auto-translate failed. Complete missing fields manually or use "Auto-translate all".',
        );
      } finally {
        setTranslationBatchLoading(false);
      }
    } else if (staleNoExperienceLanguages.length > 0) {
      setTranslationBatchLoading(true);
      try {
        const result = await translateIntoLanguages(
          noExperienceLabelDefault.trim(),
          staleNoExperienceLanguages,
        );
        noExperienceLabelTranslationState = {
          ...noExperienceLabelTranslationState,
          ...result.translations,
        };
        applyNoExperienceLabelTranslations(
          noExperienceLabelTranslationsBeforeSave,
          noExperienceLabelTranslationState,
        );
      } finally {
        setTranslationBatchLoading(false);
      }
    }

    if (requireTranslations) {
      const missingLanguage = translatableLanguages.find(
        language => !translationState[language]?.trim(),
      );
      if (missingLanguage) {
        setTextError(`Translation is required for ${missingLanguage.toUpperCase()}.`);
        return;
      }
      if (showRangeLabelsSection) {
        const missingRangeLang = translatableLanguages.find(language =>
          rangeLabelRows.some(
            row =>
              row.defaultText.trim() &&
              !(rangeLabelTranslationState[language]?.[row.id] ?? "").trim(),
          ),
        );
        if (missingRangeLang) {
          setTextError(
            `Range label translation is required for ${missingRangeLang.toUpperCase()}.`,
          );
          return;
        }
      }
      if (description.trim()) {
        const missingDescLang = translatableLanguages.find(
          language => !(descriptionTranslationState[language] ?? "").trim(),
        );
        if (missingDescLang) {
          setTextError(
            `Description translation is required for ${missingDescLang.toUpperCase()} when a hint is set.`,
          );
          return;
        }
      }
      if (showNoExperienceLabelField) {
        const missingNoExperienceLang = translatableLanguages.find(
          language => !(noExperienceLabelTranslationState[language] ?? "").trim(),
        );
        if (missingNoExperienceLang) {
          setTextError(
            `"No experience" label translation is required for ${missingNoExperienceLang.toUpperCase()}.`,
          );
          return;
        }
      }
    }

    if (showRangeLabelsSection) {
      const nonEmptyLabels = rangeLabelRows.filter(row => row.defaultText.trim());
      if (nonEmptyLabels.length < 2) {
        setRangeLabelsError("Add at least two scale labels with text.");
        return;
      }
    }

    const choiceOptionsError = getChoiceOptionsValidationError(type, availableOptions.length);
    if (choiceOptionsError) {
      setOptionsError(choiceOptionsError);
      return;
    }

    if (showRangeDefaultField && defaultValue.trim()) {
      const rangeMax = SURVEY_QUESTION_TYPE_CONFIG[type].rangeMax ?? 0;
      const parsed = Number(defaultValue);

      if (!Number.isFinite(parsed)) {
        setDefaultValueError("Default value must be a number.");
        return;
      }

      if (parsed < 0 || parsed > rangeMax) {
        setDefaultValueError(`Default value must be between 0 and ${rangeMax}.`);
        return;
      }
    }

    const parsedOptions = showOptionsField ? availableOptions : null;

    const nonEmptyRangeRows = rangeLabelRows.filter(row => row.defaultText.trim());
    const builtRangeLabels = showRangeLabelsSection
      ? nonEmptyRangeRows.map((row, index) => ({
          id: row.id,
          order: index,
          label: {
            default: row.defaultText.trim(),
            ...translatableLanguages.reduce(
              (acc, language) => {
                const translatedValue = rangeLabelTranslationState[language]?.[row.id]?.trim();
                if (translatedValue) {
                  acc[language] = translatedValue;
                }
                return acc;
              },
              {} as Record<string, string>,
            ),
          },
        }))
      : null;

    const localizedText: LocalizedQuestionText = {
      default: textDefault.trim(),
      ...translatableLanguages.reduce(
        (acc, language) => {
          const translatedValue = translationState[language]?.trim();
          if (translatedValue) {
            acc[language] = translatedValue;
          }
          return acc;
        },
        {} as Record<string, string>,
      ),
    };

    const body: CreateQuestionBody = {
      internalLabel: internalLabel.trim() || null,
      text: localizedText,
      type,
      required,
      defaultValue: defaultValue.trim() || null,
      description: buildLibraryDescriptionPayload(
        description,
        descriptionTranslationState,
        translatableLanguages,
      ),
      options: parsedOptions,
      rangeLabels: showRangeLabelsSection ? builtRangeLabels : null,
      // Only `multi-testimonial` persists a subjectLabel; null clears it for every
      // other type so the API never receives it for an unsupported question kind.
      subjectLabel: showSubjectLabelField
        ? buildSubjectLabelPayload(
            subjectLabelDefault,
            subjectLabelTranslations,
            translatableLanguages,
          )
        : null,
      noExperienceLabel: showNoExperienceLabelField
        ? buildLibraryDescriptionPayload(
            noExperienceLabelDefault,
            noExperienceLabelTranslationState,
            translatableLanguages,
          )
        : null,
      displayRules: effectiveShowDisplayRules ? effectiveDisplayRules : [],
    };

    setTextError("");
    setOptionsError("");
    setIsSaving(true);
    try {
      await Promise.resolve(onSave(body, question?.id));
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Save failed. Please try again.";
      setTextError(message);
      onSaveError?.(message);
    } finally {
      setIsSaving(false);
    }
  };

  const validateStepOne = (): boolean => {
    if (!textDefault.trim()) {
      setTextError("Question text is required");
      return false;
    }

    if (!validateNoExperienceLabel()) {
      return false;
    }

    if (showRangeLabelsSection) {
      const nonEmptyLabels = rangeLabelRows.filter(row => row.defaultText.trim());
      if (nonEmptyLabels.length < 2) {
        setRangeLabelsError("Add at least two scale labels with text.");
        return false;
      }
    }

    const choiceOptionsError = getChoiceOptionsValidationError(type, availableOptions.length);
    if (choiceOptionsError) {
      setOptionsError(choiceOptionsError);
      return false;
    }

    if (showRangeDefaultField && defaultValue.trim()) {
      const rangeMax = SURVEY_QUESTION_TYPE_CONFIG[type].rangeMax ?? 0;
      const parsed = Number(defaultValue);

      if (!Number.isFinite(parsed)) {
        setDefaultValueError("Default value must be a number.");
        return false;
      }

      if (parsed < 0 || parsed > rangeMax) {
        setDefaultValueError(`Default value must be between 0 and ${rangeMax}.`);
        return false;
      }
    }

    return true;
  };

  const missingTranslations = requireTranslations
    ? translatableLanguages.filter(language => {
        if (!translations[language]?.trim()) return true;
        if (
          shouldShowDescriptionTranslationFields &&
          description.trim() &&
          !(descriptionTranslations[language] ?? "").trim()
        ) {
          return true;
        }
        if (
          showRangeLabelsSection &&
          rangeLabelRows.some(
            row =>
              row.defaultText.trim() && !(rangeLabelTranslations[language]?.[row.id] ?? "").trim(),
          )
        ) {
          return true;
        }
        if (showNoExperienceLabelField && !(noExperienceLabelTranslations[language] ?? "").trim()) {
          return true;
        }
        return false;
      })
    : [];

  const translationsIncomplete =
    requireTranslations &&
    missingTranslations.length > 0 &&
    (!shouldUseTwoStepFlow || currentStep === 2);

  const handleNextStep = async () => {
    if (!validateStepOne()) return;
    setTextError("");
    setRangeLabelsError("");
    setOptionsError("");

    if (requireTranslations && translatableLanguages.length > 0) {
      setTranslationBatchLoading(true);
      try {
        const result = await bulkAutoTranslateQuestionFields({
          sourceText: textDefault,
          translatableLanguages,
          fillMissingOnly: false,
          baseTranslations: translations,
          baseRangeLabelTranslations: rangeLabelTranslations,
          showRangeLabelsSection,
          rangeLabelRows,
          descriptionDefault: description,
          baseDescriptionTranslations: descriptionTranslations,
          noExperienceLabelDefault: noExperienceLabelSourceForTranslate,
          baseNoExperienceLabelTranslations: noExperienceLabelTranslations,
          autoTranslateQuestionText,
        });
        setTranslations(result.mergedTranslations);
        setRangeLabelTranslations(result.mergedRangeLabelTranslations);
        setDescriptionTranslations(result.mergedDescriptionTranslations);
        applyNoExperienceLabelTranslations(
          noExperienceLabelTranslations,
          result.mergedNoExperienceLabelTranslations,
        );
        if (result.failedCount > 0) {
          setTextError(
            `Auto-translate completed with ${result.failedCount} failed request(s). You can edit translations or use "Auto-translate all".`,
          );
        }
      } catch {
        setTextError('Auto-translate failed. You can use "Auto-translate all" on step 2.');
      } finally {
        setTranslationBatchLoading(false);
      }
    }

    setCurrentStep(2);
  };

  const handleAutoTranslateLanguage = async (language: string) => {
    const sourceText = textDefault.trim();
    const hasHint = description.trim();

    if (!sourceText && !hasHint && !hasRangeSourceForAutoTranslate) {
      setTextError("Add question text, a hint, or scale labels to translate.");
      return;
    }

    setSingleAutoTranslateLoading(language);
    try {
      if (sourceText) {
        const { translatedText } = await surveysService.translate(sourceText, language);
        setTranslations(prev => ({ ...prev, [language]: translatedText }));
        if (textError) setTextError("");
      }

      if (showRangeLabelsSection) {
        const rowResults = await Promise.allSettled(
          rangeLabelRows
            .filter(row => row.defaultText.trim())
            .map(row =>
              surveysService
                .translate(row.defaultText.trim(), language)
                .then(({ translatedText }) => ({
                  rowId: row.id,
                  translatedText,
                })),
            ),
        );
        setRangeLabelTranslations(prev => {
          const next = { ...prev };
          const langMap = { ...(next[language] ?? {}) };
          for (const result of rowResults) {
            if (result.status !== "fulfilled") continue;
            langMap[result.value.rowId] = result.value.translatedText;
          }
          next[language] = langMap;
          return next;
        });
      }

      if (description.trim()) {
        const { translatedText } = await surveysService.translate(description.trim(), language);
        setDescriptionTranslations(prev => ({ ...prev, [language]: translatedText }));
      }

      if (noExperienceLabelSourceForTranslate.trim()) {
        const { translatedText } = await surveysService.translate(
          noExperienceLabelSourceForTranslate.trim(),
          language,
        );
        setNoExperienceLabelTranslations(prev => ({ ...prev, [language]: translatedText }));
        setNoExperienceLabelTranslationSources(prev => ({
          ...prev,
          [language]: noExperienceLabelSourceForTranslate.trim(),
        }));
      }
    } catch {
      setTextError(`Auto-translate failed for ${language.toUpperCase()}.`);
    } finally {
      setSingleAutoTranslateLoading(null);
    }
  };

  const handleAutoTranslateAll = async () => {
    const sourceText = textDefault.trim();
    const hasHint = description.trim();
    if (translatableLanguages.length === 0) {
      return;
    }
    if (!sourceText && !hasHint && !hasRangeSourceForAutoTranslate) {
      return;
    }
    setAutoTranslateLoading(true);
    try {
      const result = await bulkAutoTranslateQuestionFields({
        sourceText,
        translatableLanguages,
        fillMissingOnly: false,
        baseTranslations: translations,
        baseRangeLabelTranslations: rangeLabelTranslations,
        showRangeLabelsSection,
        rangeLabelRows,
        descriptionDefault: description,
        baseDescriptionTranslations: descriptionTranslations,
        noExperienceLabelDefault: noExperienceLabelSourceForTranslate,
        baseNoExperienceLabelTranslations: noExperienceLabelTranslations,
        // User explicitly requested translation — always include question text when present.
        autoTranslateQuestionText: true,
      });
      setTranslations(result.mergedTranslations);
      setRangeLabelTranslations(result.mergedRangeLabelTranslations);
      setDescriptionTranslations(result.mergedDescriptionTranslations);
      applyNoExperienceLabelTranslations(
        noExperienceLabelTranslations,
        result.mergedNoExperienceLabelTranslations,
      );
      if (result.failedCount > 0) {
        setTextError(`Auto-translate completed with ${result.failedCount} failed request(s).`);
      } else if (textError) {
        setTextError("");
      }
    } finally {
      setAutoTranslateLoading(false);
    }
  };

  const handleAutoTranslateSubjectLabel = async (language: string) => {
    const source = subjectLabelDefault.trim();
    if (!source) {
      return;
    }
    setSubjectLabelTranslatingLanguage(language);
    try {
      const { translatedText } = await surveysService.translate(source, language);
      setSubjectLabelTranslations(prev => ({ ...prev, [language]: translatedText }));
    } catch {
      setTextError(`Auto-translate failed for subject label (${language.toUpperCase()}).`);
    } finally {
      setSubjectLabelTranslatingLanguage(null);
    }
  };

  const handleToggleNoExperience = (checked: boolean) => {
    setNoExperienceEnabled(checked);
    setNoExperienceLabelError("");
    if (checked && !noExperienceLabelDefault.trim()) {
      setNoExperienceLabelDefault(DEFAULT_NO_EXPERIENCE_LABEL);
    }
  };

  const handleClose = () => {
    setTextError("");
    setDefaultValueError("");
    setRangeLabelsError("");
    setOptionsError("");
    setIsSaving(false);
    setTranslationBatchLoading(false);
    onClose();
  };

  const addOptionRow = () => {
    const trimmedOption = newOption.trim();
    if (!trimmedOption) return;
    if (optionRows.some(option => option.trim() === trimmedOption)) {
      setNewOption("");
      return;
    }
    setOptionRows(prev => [...prev, trimmedOption]);
    setNewOption("");
    setOptionsError("");
  };

  const removeOptionRow = (optionToRemove: string) => {
    setOptionRows(prev => prev.filter(option => option !== optionToRemove));
    setOptionsError("");
    if (defaultValue === optionToRemove) {
      setDefaultValue("");
    }
  };

  // Fully clears the description: both the default hint and every per-language
  // translation, so buildLibraryDescriptionPayload returns null (persisting
  // `description: null`). Needed because that builder otherwise promotes a leftover
  // translation to `default`, making the description impossible to remove by only
  // blanking the default field.
  const handleClearDescription = () => {
    setDescription("");
    setDescriptionTranslations(
      translatableLanguages.reduce(
        (acc, language) => {
          acc[language] = "";
          return acc;
        },
        {} as Record<string, string>,
      ),
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={question ? "Edit Question" : "Add Question"}
      size="mediumLarge"
      backdropClick={false}
    >
      <div className={styles.modalForm}>
        <div className={styles.internalLabelSection}>
          <div className={styles.formFieldFull}>
            <Input
              label="Internal label"
              value={internalLabel}
              onChange={event => setInternalLabel(event.target.value)}
              fullWidth
              placeholder="Optional internal identifier"
            />
            <div className={styles.internalLabelHint}>
              Internal only. This label is not visible to respondents.
            </div>
          </div>
        </div>

        <div className={styles.formFieldFull}>
          <label className={styles.fieldLabel}>
            Question Text <span aria-hidden="true">*</span>
          </label>
          <textarea
            className={`${styles.textareaField}${textError ? ` ${styles.textareaFieldError}` : ""}`}
            rows={3}
            value={textDefault}
            onChange={e => {
              setTextDefault(e.target.value);
              if (textError) setTextError("");
            }}
            placeholder="Enter question text"
            required
          />
          {textError && <div className={styles.fieldError}>{textError}</div>}
        </div>

        {(!shouldUseTwoStepFlow || currentStep === 1) && (
          <>
            <div className={styles.requiredField}>
              <Checkbox
                id="question-required"
                label="Answer required"
                checked={required}
                onChange={event => setRequired(event.target.checked)}
              />
            </div>

            <div className={styles.formRowTypeAndDefault}>
              <div className={styles.formField}>
                <Select
                  label="Type"
                  options={SURVEY_QUESTION_TYPE_OPTIONS}
                  value={type}
                  onChange={e => setType(e.target.value as QuestionType)}
                  fullWidth
                />
              </div>
              <div className={styles.formField}>
                {showOptionsField ? (
                  <Select
                    label="Default Value"
                    options={[
                      { value: "", label: "None" },
                      ...availableOptions.map(option => ({ value: option, label: option })),
                    ]}
                    value={defaultValue}
                    onChange={e => setDefaultValue(e.target.value)}
                    fullWidth
                  />
                ) : showYesNoDefaultField ? (
                  <Select
                    label="Default Value"
                    options={[{ value: "", label: "None" }, ...YES_NO_DEFAULT_OPTIONS]}
                    value={defaultValue}
                    onChange={e => setDefaultValue(e.target.value)}
                    fullWidth
                  />
                ) : showRangeDefaultField ? (
                  <Input
                    label="Default Value"
                    value={defaultValue}
                    onChange={e => setDefaultValue(e.target.value)}
                    fullWidth
                    placeholder={`Optional (0 to ${SURVEY_QUESTION_TYPE_CONFIG[type].rangeMax})`}
                    error={defaultValueError}
                  />
                ) : (
                  <Input
                    label="Default Value"
                    value={defaultValue}
                    onChange={e => setDefaultValue(e.target.value)}
                    fullWidth
                    placeholder="Optional"
                  />
                )}
              </div>
            </div>

            {showOptionsField && (
              <div className={styles.formFieldFull}>
                <label className={styles.fieldLabel}>
                  Options <span aria-hidden="true">*</span>
                </label>
                <span className={styles.rangeLabelsHint}>At least two options required.</span>
                {optionsError && <div className={styles.fieldError}>{optionsError}</div>}
                <div className={styles.optionChips}>
                  {availableOptions.map(option => (
                    <Chip
                      key={option}
                      size="small"
                      variant="outlined"
                      className={styles.optionChipPurple}
                      onDelete={() => removeOptionRow(option)}
                    >
                      {option}
                    </Chip>
                  ))}
                </div>
                <div className={styles.optionInputRow}>
                  <Input
                    value={newOption}
                    onChange={e => setNewOption(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addOptionRow();
                      }
                    }}
                    fullWidth
                    placeholder="Add option and press Enter"
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={addOptionRow}
                    startIcon={<Icon icon="plus" size="small" />}
                    disabled={!newOption.trim()}
                  >
                    Add
                  </Button>
                </div>
              </div>
            )}

            <DescriptionField
              value={description}
              onChange={setDescription}
              showClear={descriptionHasContent}
              onClear={handleClearDescription}
            />

            {showSubjectLabelField && (
              <SubjectLabelField
                defaultText={subjectLabelDefault}
                onChangeDefault={setSubjectLabelDefault}
                translatableLanguages={translatableLanguages}
                translations={subjectLabelTranslations}
                onChangeTranslation={(language, value) =>
                  setSubjectLabelTranslations(prev => ({ ...prev, [language]: value }))
                }
                onAutoTranslateLanguage={language => void handleAutoTranslateSubjectLabel(language)}
                autoTranslatingLanguage={subjectLabelTranslatingLanguage}
              />
            )}

            {showRangeLabelsSection && (
              <div className={styles.rangeLabelsSection}>
                <div className={styles.rangeLabelsSectionHeader}>
                  <label className={styles.fieldLabel}>Scale labels</label>
                  <span className={styles.rangeLabelsHint}>
                    At least two labels with text. Drag to reorder.
                  </span>
                </div>
                {rangeLabelsError && <div className={styles.fieldError}>{rangeLabelsError}</div>}
                <DndContext
                  sensors={labelDragSensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleRangeLabelsDragEnd}
                >
                  <SortableContext
                    items={rangeLabelRows.map(row => row.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className={styles.rangeLabelList}>
                      {rangeLabelRows.map(row => (
                        <SortableRangeLabelEditor
                          key={row.id}
                          id={row.id}
                          row={row}
                          onChangeDefault={value => {
                            setRangeLabelRows(prev =>
                              prev.map(r => (r.id === row.id ? { ...r, defaultText: value } : r)),
                            );
                            setRangeLabelsError("");
                          }}
                          onRemove={() => {
                            setRangeLabelRows(prev => prev.filter(r => r.id !== row.id));
                            setRangeLabelTranslations(prev => {
                              const next = { ...prev };
                              for (const lang of Object.keys(next)) {
                                const copy = { ...(next[lang] ?? {}) };
                                delete copy[row.id];
                                next[lang] = copy;
                              }
                              return next;
                            });
                          }}
                          canRemove={rangeLabelRows.length > 2}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    const row = createRangeLabelRow();
                    setRangeLabelRows(prev => [...prev, row]);
                  }}
                  startIcon={<Icon icon="plus" size="small" />}
                >
                  Add label
                </Button>
              </div>
            )}

            {showNoExperienceSection && (
              <div className={styles.noExperienceSection}>
                <Checkbox
                  id="question-no-experience"
                  aria-label={'Show "No experience" option'}
                  label={'Show "No experience" option'}
                  checked={noExperienceEnabled}
                  onChange={event => handleToggleNoExperience(event.target.checked)}
                />
                {showNoExperienceLabelField && (
                  <div className={styles.noExperienceField}>
                    <div className={styles.rangeLabelsSectionHeader}>
                      <label className={styles.fieldLabel} htmlFor="no-experience-label-default">
                        "No experience" label
                      </label>
                      <span className={styles.rangeLabelsHint}>
                        Shown as a checkbox under the slider. Ticking it satisfies a required
                        question and saves the answer as "no_experience" instead of a 0–100 score,
                        so it is excluded from score averages.
                      </span>
                    </div>
                    <Input
                      id="no-experience-label-default"
                      value={noExperienceLabelDefault}
                      onChange={event => {
                        setNoExperienceLabelDefault(event.target.value);
                        setNoExperienceLabelError("");
                      }}
                      fullWidth
                      placeholder={DEFAULT_NO_EXPERIENCE_LABEL}
                      error={noExperienceLabelError || undefined}
                    />
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {(!shouldUseTwoStepFlow || currentStep === 2) && translatableLanguages.length > 0 && (
          <div className={styles.formFieldFull}>
            <div className={styles.translationHeader}>
              <label className={styles.fieldLabel}>Translations</label>
              <Button
                variant="outlined"
                size="small"
                onClick={handleAutoTranslateAll}
                disabled={
                  autoTranslateLoading ||
                  (!textDefault.trim() && !description.trim() && !hasRangeSourceForAutoTranslate)
                }
              >
                {autoTranslateLoading ? "Translating..." : "Auto-translate all"}
              </Button>
            </div>
            <div className={styles.translationList}>
              {translatableLanguages.map(language => (
                <div key={language} className={styles.translationField}>
                  <div className={styles.translationFieldHeader}>
                    <label
                      className={styles.translationLabel}
                      htmlFor={`question-translation-${language}`}
                    >
                      {language.toUpperCase()} {getLanguageFlag(language)}
                    </label>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleAutoTranslateLanguage(language)}
                      disabled={
                        singleAutoTranslateLoading === language ||
                        (!textDefault.trim() &&
                          !description.trim() &&
                          !hasRangeSourceForAutoTranslate)
                      }
                    >
                      {singleAutoTranslateLoading === language
                        ? "Translating..."
                        : "Auto-translate"}
                    </Button>
                  </div>
                  <textarea
                    id={`question-translation-${language}`}
                    className={styles.translationTextarea}
                    value={translations[language] ?? ""}
                    onChange={event => {
                      setTranslations(prev => ({ ...prev, [language]: event.target.value }));
                      if (textError) setTextError("");
                    }}
                    placeholder={`Enter ${language.toUpperCase()} translation`}
                  />
                  {shouldShowDescriptionTranslationFields && (
                    <div className={styles.rangeLabelTranslationSubfield}>
                      <label
                        className={styles.rangeLabelTranslationSubLabel}
                        htmlFor={`question-desc-translation-${language}`}
                      >
                        Description / hint
                      </label>
                      <textarea
                        id={`question-desc-translation-${language}`}
                        className={styles.translationTextarea}
                        value={descriptionTranslations[language] ?? ""}
                        onChange={event => {
                          setDescriptionTranslations(prev => ({
                            ...prev,
                            [language]: event.target.value,
                          }));
                          if (textError) setTextError("");
                        }}
                        placeholder={
                          requireTranslations && description.trim()
                            ? `${language.toUpperCase()} hint (required when default hint is set)`
                            : `${language.toUpperCase()} hint (optional)`
                        }
                      />
                    </div>
                  )}
                  {showRangeLabelsSection &&
                    rangeLabelRows.map(row => (
                      <div
                        key={`${language}-${row.id}`}
                        className={styles.rangeLabelTranslationSubfield}
                      >
                        <label
                          className={styles.rangeLabelTranslationSubLabel}
                          htmlFor={`question-rl-${language}-${row.id}`}
                        >
                          Scale label ({row.defaultText.trim() || "empty"})
                        </label>
                        <textarea
                          id={`question-rl-${language}-${row.id}`}
                          className={styles.translationTextarea}
                          value={rangeLabelTranslations[language]?.[row.id] ?? ""}
                          onChange={event => {
                            setRangeLabelTranslations(prev => ({
                              ...prev,
                              [language]: {
                                ...(prev[language] ?? {}),
                                [row.id]: event.target.value,
                              },
                            }));
                            if (textError) setTextError("");
                          }}
                          placeholder={`${language.toUpperCase()} translation for this label`}
                        />
                      </div>
                    ))}
                  {showNoExperienceLabelField && (
                    <div className={styles.rangeLabelTranslationSubfield}>
                      <label
                        className={styles.rangeLabelTranslationSubLabel}
                        htmlFor={`question-no-experience-${language}`}
                      >
                        "No experience" label
                      </label>
                      <textarea
                        id={`question-no-experience-${language}`}
                        className={styles.translationTextarea}
                        value={noExperienceLabelTranslations[language] ?? ""}
                        onChange={event => {
                          setNoExperienceLabelTranslations(prev => ({
                            ...prev,
                            [language]: event.target.value,
                          }));
                          setNoExperienceLabelTranslationSources(prev => ({
                            ...prev,
                            [language]: noExperienceLabelDefault.trim(),
                          }));
                          if (textError) setTextError("");
                        }}
                        placeholder={`${language.toUpperCase()} translation for the "No experience" label`}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
            {translationsIncomplete && (
              <div className={styles.translationValidationBanner} role="alert">
                <strong>Complete all required translations before saving.</strong> Missing for:{" "}
                {missingTranslations.map(language => language.toUpperCase()).join(", ")}.
              </div>
            )}
          </div>
        )}

        {effectiveShowDisplayRules && (
          <FilterRulesEditor
            ref={displayRulesEditorRef}
            rules={displayRules}
            onChange={setDisplayRules}
            taxonomyOptions={taxonomyOptions}
          />
        )}

        {shouldUseTwoStepFlow && (
          <div className={styles.modalStepIndicator}>
            Step {currentStep} of 2: {currentStep === 1 ? "Question details" : "Translations"}
          </div>
        )}

        <div className={styles.modalFooter}>
          <Button variant="outlined" size="small" onClick={handleClose}>
            Cancel
          </Button>
          {shouldUseTwoStepFlow && currentStep === 2 && (
            <Button variant="outlined" size="small" onClick={() => setCurrentStep(1)}>
              Back
            </Button>
          )}
          {shouldUseTwoStepFlow && currentStep === 1 ? (
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => void handleNextStep()}
              disabled={translationBatchLoading}
            >
              {translationBatchLoading ? "Translating..." : "Next"}
            </Button>
          ) : (
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => void handleSave()}
              disabled={translationsIncomplete || isSaving || translationBatchLoading}
            >
              {isSaving
                ? "Saving..."
                : translationBatchLoading
                  ? "Translating..."
                  : question
                    ? "Save"
                    : (createModeSaveLabel ?? "Add to Library")}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}

export { QuestionFormModal };
